/** Shared Tcheck UI pieces: wordmark, stepper, alerts, cards. */
import { AlertTriangle, Check, CircleAlert, Info, Loader2 } from 'lucide-react';

export const Wordmark = ({ className = '' }: { className?: string }) => (
  <span
    data-testid="tcheck-wordmark"
    className={`font-bold tracking-tight bg-gradient-to-r from-cyan-600 via-indigo-600 to-slate-800 bg-clip-text text-transparent ${className}`}
  >
    Tcheck
  </span>
);

export const STEPS = [
  { key: 'cuenta', label: 'Cuenta' },
  { key: 'dispositivo', label: 'Dispositivo' },
  { key: 'rostro', label: 'Rostro' },
  { key: 'voz', label: 'Voz' },
  { key: 'palma', label: 'Palma' },
  { key: 'completada', label: 'Listo' },
] as const;

export type StepKey = (typeof STEPS)[number]['key'];

/** Unmistakable, always-visible warning while BIOMETRIC_SYNTHETIC_MODE is on. */
export const SyntheticBanner = () => (
  <div
    data-testid="synthetic-mode-banner"
    role="alert"
    className="fixed inset-x-0 top-0 z-50 flex items-center justify-center gap-2 bg-amber-500 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-slate-900 shadow-md"
  >
    <AlertTriangle size={14} />
    Modo sintético — resultados de prueba, no válidos como verificación biométrica
  </div>
);

export const Stepper = ({ done, current }: { done: StepKey[]; current: StepKey }) => (
  <nav data-testid="onboarding-stepper" aria-label="Progreso de registro" className="w-full">
    <ol className="flex items-center gap-1 overflow-x-auto pb-1">
      {STEPS.map((step, index) => {
        const isDone = done.includes(step.key);
        const isCurrent = step.key === current;
        return (
          <li key={step.key} className="flex items-center gap-1 shrink-0">
            <span
              data-testid={`step-${step.key}`}
              data-state={isDone ? 'done' : isCurrent ? 'current' : 'pending'}
              className={[
                'flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors',
                isDone
                  ? 'bg-emerald-50 text-emerald-700'
                  : isCurrent
                    ? 'bg-cyan-50 text-cyan-700 ring-1 ring-cyan-200'
                    : 'bg-slate-100 text-slate-500',
              ].join(' ')}
            >
              {isDone ? (
                <Check size={13} strokeWidth={3} />
              ) : (
                <span className="text-[10px] font-semibold">{index + 1}</span>
              )}
              {step.label}
            </span>
            {index < STEPS.length - 1 && <span className="h-px w-3 bg-slate-200" />}
          </li>
        );
      })}
    </ol>
  </nav>
);

export const Card = ({
  children,
  className = '',
  testId,
}: {
  children: React.ReactNode;
  className?: string;
  testId?: string;
}) => (
  <section
    data-testid={testId}
    className={`rounded-2xl border border-slate-200 bg-white p-6 shadow-[0_1px_2px_rgba(16,24,40,0.04)] ${className}`}
  >
    {children}
  </section>
);

type Tone = 'info' | 'success' | 'warning' | 'error';

const TONES: Record<Tone, { cls: string; Icon: typeof Info }> = {
  info: { cls: 'bg-slate-50 text-slate-700 border-slate-200', Icon: Info },
  success: { cls: 'bg-emerald-50 text-emerald-800 border-emerald-200', Icon: Check },
  warning: { cls: 'bg-amber-50 text-amber-900 border-amber-200', Icon: AlertTriangle },
  error: { cls: 'bg-red-50 text-red-800 border-red-200', Icon: CircleAlert },
};

export const Alert = ({
  tone = 'info',
  children,
  testId,
}: {
  tone?: Tone;
  children: React.ReactNode;
  testId?: string;
}) => {
  const { cls, Icon } = TONES[tone];
  return (
    <div
      data-testid={testId}
      role={tone === 'error' ? 'alert' : 'status'}
      className={`flex items-start gap-2.5 rounded-xl border px-4 py-3 text-sm ${cls}`}
    >
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div className="leading-relaxed">{children}</div>
    </div>
  );
};

export const Button = ({
  children,
  variant = 'primary',
  loading = false,
  className = '',
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'danger' | 'ghost';
  loading?: boolean;
}) => {
  const styles = {
    primary: 'bg-[#00A8B5] text-white hover:bg-[#008A95] disabled:bg-slate-300',
    secondary: 'border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-slate-50',
    danger: 'border border-red-200 text-red-700 hover:bg-red-50',
    ghost: 'text-slate-600 hover:bg-slate-100',
  }[variant];
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={`inline-flex items-center justify-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-colors disabled:cursor-not-allowed ${styles} ${className}`}
    >
      {loading && <Loader2 size={15} className="animate-spin" />}
      {children}
    </button>
  );
};

export const Field = ({
  label,
  hint,
  testId,
  ...rest
}: React.InputHTMLAttributes<HTMLInputElement> & {
  label: string;
  hint?: string;
  testId: string;
}) => (
  <label className="block">
    <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{label}</span>
    <input
      {...rest}
      data-testid={testId}
      className="mt-1.5 w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-[#00A8B5] focus:ring-2 focus:ring-cyan-100"
    />
    {hint && <span className="mt-1 block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const PilotBadge = ({ label = 'PILOTO' }: { label?: string }) => (
  <span
    data-testid="pilot-badge"
    className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700"
  >
    {label}
  </span>
);

const STATUS_TONES: Record<string, string> = {
  APPROVED: 'bg-cyan-100 text-cyan-800',
  ACTIVE: 'bg-cyan-100 text-cyan-800',
  DENIED: 'bg-rose-100 text-rose-700',
  EXPIRED: 'bg-slate-200 text-slate-600',
  LOCKED: 'bg-rose-100 text-rose-700',
  ERROR: 'bg-rose-100 text-rose-700',
  STEP_UP_REQUIRED: 'bg-amber-100 text-amber-800',
};

export const StatusPill = ({ status, label }: { status: string; label?: string }) => (
  <span
    data-testid="status-pill"
    className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
      STATUS_TONES[status] ?? 'bg-slate-100 text-slate-600'
    }`}
  >
    {label ?? status}
  </span>
);

